<script setup lang="ts">
import { reactive, ref } from "vue";
import { useResponseError } from "~/composables/error";

const { $api } = useNuxtApp();
const { t } = useI18n();
const toast = useToast();
const { formErrors, errorMsg, setErrors, clearErrors } = useResponseError();

const open = ref(false);
const adminId = ref<string | null>(null);

const form = reactive({
  newPassword: "",
  rePassword: "",
});
const saveLoading = ref(false);

function reset() {
  form.newPassword = "";
  form.rePassword = "";
  clearErrors();
  saveLoading.value = false;
}

function openModal(id: string) {
  reset();
  adminId.value = id;
  open.value = true;
}

function close() {
  open.value = false;
  adminId.value = null;
  reset();
}

async function submit() {
  if (!adminId.value) return;
  clearErrors();

  if (form.newPassword !== form.rePassword) {
    formErrors.value = { rePassword: t("adminUser.errorPasswordMismatch") };
    return;
  }

  saveLoading.value = true;
  try {
    await $api("/admin-users/password", {
      method: "PUT",
      body: {
        id: adminId.value,
        newPassword: form.newPassword,
        rePassword: form.rePassword,
      },
    });
    toast.success({ title: t("adminUser.resetPasswordSuccess") });
    close();
  } catch (e: any) {
    setErrors(e);
    if (errorMsg.value && Object.keys(formErrors.value).length === 0) {
      toast.error({ title: errorMsg.value });
    }
  } finally {
    saveLoading.value = false;
  }
}

defineExpose({
  open: openModal,
  close,
});
</script>

<template>
  <Modal
    :open="open"
    :title="t('adminUser.resetPasswordTitle')"
    size="sm"
    @update:open="(v: boolean) => (open = v)"
    @close="close"
  >
    <!-- autocomplete="off" + unique field names so password managers don't
         try to autofill (or write back into) the parent list's search input. -->
    <form
      class="flex flex-col gap-medium"
      autocomplete="off"
      data-testid="reset-password-form"
      @submit.prevent="submit"
    >
      <FormField
        v-model="form.newPassword"
        type="password"
        name="reset-new-password"
        autocomplete="new-password"
        :label="t('field.newPassword')"
        :error="formErrors.newPassword"
      />

      <FormField
        v-model="form.rePassword"
        type="password"
        name="reset-confirm-password"
        autocomplete="new-password"
        :label="t('field.confirmPassword')"
        :error="formErrors.rePassword"
      />
    </form>

    <template #actions>
      <Button variant="tertiary" size="small" @click="close">
        {{ t("action.cancel") }}
      </Button>
      <Button
        variant="primary"
        size="small"
        :disabled="saveLoading"
        data-testid="reset-password-save"
        @click="submit"
      >
        <ProgressCircle v-if="saveLoading" size="small" />
        {{ t("action.save") }}
      </Button>
    </template>
  </Modal>
</template>
